import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HeartHandshake, AlertCircle, ArrowRight, PartyPopper, Flag } from "lucide-react";
import client from "../../lib/api";

/**
 * RoadmapNudges.
 *
 * Klair's gentle accountability read on the dashboard. Surfaces the
 * next stop, anything overdue, and a recent win, across every goal
 * that has a roadmap. Renders nothing when there is nothing to say.
 */
export function RoadmapNudges({ base }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    client.get("/engagement/roadmap/nudges")
      .then((r) => setData(r?.data || null))
      .catch(() => setData(null));
  }, []);

  if (!data || !data.has_any) return null;
  const { next_up = [], overdue = [], wins = [] } = data;

  return (
    <div className="k-card mt-5" data-testid="roadmap-nudges">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: "hsl(var(--klareet-teal))", color: "#fff" }}><HeartHandshake size={16} /></span>
        <div>
          <div className="k-label !mb-0">Klair, keeping you honest</div>
          <div className="k-quiet !text-xs">{data.line}</div>
        </div>
      </div>

      <div className="space-y-2">
        {overdue.map((n) => (
          <Link
            key={`o-${n.goal_id}-${n.phase_id}`}
            to={`${base}/roadmap?goal=${n.goal_id}`}
            className="flex items-center gap-3 rounded-xl p-3 transition-all hover:-translate-y-0.5"
            style={{ background: "hsl(28 90% 96%)", border: "1px solid hsl(28 78% 85%)" }}
            data-testid={`nudge-overdue-${n.phase_id}`}
          >
            <AlertCircle size={16} className="shrink-0" style={{ color: "hsl(28 74% 45%)" }} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate" style={{ color: "hsl(var(--klareet-ink))" }}>{n.phase_title}</div>
              <div className="k-quiet !text-xs truncate">{n.goal_title} · was due {n.reminder_on}</div>
            </div>
            <ArrowRight size={14} style={{ color: "hsl(28 74% 45%)" }} />
          </Link>
        ))}
        {next_up.map((n) => (
          <Link
            key={`n-${n.goal_id}-${n.phase_id}`}
            to={`${base}/roadmap?goal=${n.goal_id}`}
            className="flex items-center gap-3 rounded-xl p-3 transition-all hover:-translate-y-0.5"
            style={{ background: "hsl(var(--klareet-canvas-2))", border: "1px solid hsl(var(--klareet-line-soft))" }}
            data-testid={`nudge-next-${n.phase_id}`}
          >
            <Flag size={16} className="shrink-0" style={{ color: "hsl(var(--klareet-teal))" }} />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate" style={{ color: "hsl(var(--klareet-ink))" }}>{n.phase_title}</div>
              <div className="k-quiet !text-xs truncate">{n.goal_title} · {n.window}</div>
            </div>
            <ArrowRight size={14} className="opacity-50" />
          </Link>
        ))}
        {wins.map((w, i) => (
          <div
            key={`w-${i}`}
            className="flex items-center gap-3 rounded-xl p-3"
            style={{ background: "hsl(var(--klareet-teal) / 0.07)", border: "1px solid hsl(var(--klareet-teal) / 0.22)" }}
            data-testid={`nudge-win-${i}`}
          >
            <PartyPopper size={16} className="shrink-0" style={{ color: "hsl(var(--klareet-teal))" }} />
            <div className="min-w-0 flex-1">
              <div className="k-quiet !text-xs">{w.message}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default RoadmapNudges;
